import { Box, Typography } from '@mui/material';
import { DateTime } from 'luxon';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { CopyButton } from '../../components/CopyButton';
import { FilledTextField } from '../../components/FilledTextField';
import { RegenerateButton } from '../../components/RegenerateButton';
import { OrganizationSecret, OrganizationServiceAccount } from '../../services/organization/types';
import { OrganizationId } from '../../types/idFlavors';
import { formatDate } from '../../utils';

const styles = {
    container: { display: 'flex', justifyContent: 'space-between', margin: '24px', flexDirection: 'column' },
    clientSection: {
        paddingBottom: 2,
    },
    tokenSection: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: '16px',
    },
    textInputStyle: { width: '100%', marginBottom: '0px', marginRight: '0px' },
    btnStyle: { textTransform: 'uppercase', borderRadius: '5px', width: '100%' },
};

type OrgServiceAccountViewProps = {
    orgServiceAccount: OrganizationServiceAccount;
    organizationId: OrganizationId;
    organizationName: string;
    setOrgServiceAccount: (updatedSecrets: OrganizationServiceAccount) => void;
    isUserOwner: boolean;
};
export const OrgServiceAccountView: React.FC<OrgServiceAccountViewProps> = (props) => {
    const { orgServiceAccount, organizationId, organizationName, setOrgServiceAccount, isUserOwner } = props;
    //sorting added to ensure that secret1 is always at index 0
    const organizationSecret = React.useMemo(() => {
        if (!orgServiceAccount?.clientSecrets) return [];
        // Sort so that 'secret1' is always at index 0
        return [...orgServiceAccount.clientSecrets].sort((a, b) => {
            if (a.name === 'secret1') return -1;
            if (b.name === 'secret1') return 1;
            return 0;
        });
    }, [orgServiceAccount]);
    const { t } = useTranslation();

    const updateSecret = (updatedSecret: OrganizationSecret, secretIndex: number): void => {
        if (!orgServiceAccount) {
            return;
        }

        const updatedSecrets = organizationSecret.map((currentSecret, currentSecretIndex) => {
            if (currentSecretIndex === secretIndex) {
                return updatedSecret;
            }
            return currentSecret;
        });

        setOrgServiceAccount({ ...orgServiceAccount, clientSecrets: updatedSecrets });
    };
    return (
        <Box sx={styles.container}>
            <Box sx={styles.clientSection}>
                <Typography variant="h6">{t('ORGANIZATION_DETAILS_PAGE.ORGANIZATION_CREDENTIALS')}</Typography>
            </Box>
            <FilledTextField
                sx={styles.textInputStyle}
                data-testid="org-id-text"
                label={t('ORGANIZATION_DETAILS_PAGE.ORGANIZATION_ID')}
                value={organizationId}
                InputProps={{
                    readOnly: true,
                    endAdornment: <CopyButton content={organizationId} copyBtnStyle={styles.btnStyle} />,
                }}
            />
            <FilledTextField
                sx={styles.textInputStyle}
                data-testid="client-id-text"
                label={t('ORGANIZATION_DETAILS_PAGE.CLIENT_ID')}
                value={orgServiceAccount.clientId}
                InputProps={{
                    readOnly: true,
                    endAdornment: <CopyButton content={orgServiceAccount.clientId} copyBtnStyle={styles.btnStyle} />,
                }}
            />
            <Box sx={styles.tokenSection}>
                <FilledTextField
                    sx={styles.textInputStyle}
                    data-testid="secret1-text"
                    label={`${t('ORGANIZATION_DETAILS_PAGE.SECRET')} 1 ${t('ORGANIZATION_DETAILS_PAGE.EXPIRY')}`}
                    value={formatDate(DateTime.fromISO(organizationSecret[0].expiration))}
                    InputProps={{
                        readOnly: true,
                        endAdornment: isUserOwner && (
                            <RegenerateButton
                                btnStyle={styles.btnStyle}
                                secretName={organizationSecret[0].name}
                                organizationName={organizationName}
                                organizationId={organizationId}
                                setSecret={(updatedSecret): void => {
                                    updateSecret(updatedSecret, 0);
                                }}
                            />
                        ),
                    }}
                />

                <FilledTextField
                    sx={styles.textInputStyle}
                    data-testid="secret2-text"
                    label={`${t('ORGANIZATION_DETAILS_PAGE.SECRET')} 2 ${t('ORGANIZATION_DETAILS_PAGE.EXPIRY')}`}
                    value={formatDate(DateTime.fromISO(organizationSecret[1].expiration))}
                    InputProps={{
                        readOnly: true,
                        endAdornment: isUserOwner && (
                            <RegenerateButton
                                btnStyle={styles.btnStyle}
                                secretName={organizationSecret[1].name}
                                organizationName={organizationName}
                                organizationId={organizationId}
                                setSecret={(updatedSecret): void => {
                                    updateSecret(updatedSecret, 1);
                                }}
                            />
                        ),
                    }}
                />
            </Box>
        </Box>
    );
};

export default OrgServiceAccountView;
